/**
 * The landing overview: revenue, and only revenue.
 *
 * It used to carry a second section previewing the two most recent
 * auto-generated winner posters, which was the same latest-2 slice
 * `Posters.tsx` already renders under its `winner_generated` group. Two
 * screens showing one list is two places to keep in step and one more thing
 * for an operator to wonder about; the poster preview lives with the rest of
 * the posters.
 *
 * Still gated rather than shown to everyone — revenue is a `finance`/`auditor`
 * concern (`ledger.read`, the same permission `/admin/reconcile` uses), and
 * the usual "server decides, this decides what to render" rule applies. A
 * role without it now lands on an empty dashboard, which is honest: its work
 * is on the screens in the nav, not here.
 *
 * Every figure below is real money from real players. Phantom test accounts
 * are excluded at the query, not by convention — see `domain/reports/index.ts`
 * for how, and why it is done there rather than trusted to hold upstream.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { can } from '../auth.ts';
import { etb } from '../format.ts';
import { ErrorBanner, Spinner, Empty } from '../components/ui.tsx';
import type { AdminView, RevenueReport } from '../api/types.ts';

/** `etb()` rejects negatives (santim is non-negative by design) — net cash flow can go either way. */
function signedEtb(amount: string): string {
  const v = BigInt(amount);
  const abs = (v < 0n ? -v : v).toString();
  return (v < 0n ? '-' : '') + etb(abs);
}

export function Dashboard({ me }: { me: AdminView }) {
  const showRevenue = can(me.role, 'ledger.read');

  return (
    <section>
      <header className="pagehead">
        <h1>Dashboard</h1>
      </header>
      {showRevenue ? <RevenueSection /> : <Empty>Nothing to show for your role.</Empty>}
    </section>
  );
}

function RevenueSection() {
  const [report, setReport] = useState<RevenueReport | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReport(await api.revenueReport());
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="card">
      <h2>Revenue</h2>
      <ErrorBanner error={error} />
      {report === null && !error ? <Spinner /> : null}
      {report ? (
        <>
          <div className="revenue__total">
            <span className="revenue__totallabel">Total earnings (commission)</span>
            <strong className="revenue__totalvalue">{etb(report.totalCommission)}</strong>
          </div>

          <div className="statgrid">
            <Stat label="Real players" value={report.realUserCount.toLocaleString()} />
            <Stat label="Total deposited" value={etb(report.totalDeposited)} />
            <Stat label="Total withdrawn" value={etb(report.totalWithdrawn)} />
            <Stat label="Net cash flow" value={signedEtb(report.netCashFlow)} />
          </div>

          <div className="divider" />
          <h3>By month</h3>
          <table className="table" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th scope="col" role="columnheader">Month</th>
                <th scope="col" role="columnheader">Commission</th>
                <th scope="col" role="columnheader">Deposited</th>
                <th scope="col" role="columnheader">Withdrawn</th>
                <th scope="col" role="columnheader">New players</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {report.monthly.map((m) => (
                <tr role="row" key={m.month}>
                  <td role="cell" data-label="Month">{m.month}</td>
                  <td role="cell" data-label="Commission">{etb(m.commission)}</td>
                  <td role="cell" data-label="Deposited">{etb(m.deposited)}</td>
                  <td role="cell" data-label="Withdrawn">{etb(m.withdrawn)}</td>
                  <td role="cell" data-label="New players">{m.newRealUsers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
    </div>
  );
}
